'use client';

import { useState } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { toast } from 'react-toastify';

// Apis
import {
  useGetListPropertiesOnlineProfile,
  useGetOnlineProfile,
} from '@src/apis/online-profile/query';
import { useQueryPropertyCategories } from '@src/apis/shared/get-property-categories';
import { useSubmitLeads } from '@src/apis/shared/mutation/use-post-leads';

const OnlineProfileCtx = createContext();

const LIMIT = 32;

const initFilterForm = {
  'price[min]': null,
  'price[max]': null,
  bedroom: null,
  additional_bedroom: null,
  bathroom: null,
  additional_bathroom: null,
  'surface_area[min]': null,
  'surface_area[max]': null,
  'building_area[min]': null,
  'building_area[max]': null,
  _order: null,
  property_category_id: null,
};

const initSearchForm = {
  area_id: null,
  city_id: null,
  keywords: null,
};

const initForm = {
  ...initFilterForm,
  ...initSearchForm,
  page: 1,
  per_page: LIMIT,
};

export const OnlineProfileProvider = ({ children }) => {
  const { id } = useParams();

  const [filter, setFilter] = useState({ ...initForm });

  const { mutate: submitLeads } = useSubmitLeads();
  const { data: onlineProfile, isLoading: isLoadingOnlineProfile } =
    useGetOnlineProfile({ id, options: { enabled: !!id } });
  const userID = onlineProfile?.data?.data?.id;

  const { data: listPropertyCategories } = useQueryPropertyCategories();

  const {
    data: listListing,
    fetchNextPage: fetchNextPageListListing,
    hasNextPage: hasNextPageListListing,
    isLoading: isLoadingListListing,
    isFetchingNextPage: isFetchingNextPageListListing,
  } = useGetListPropertiesOnlineProfile({ id: id, params: filter, options: { enabled: !!id } });

  const handleSubmitLeads = (form, callback) => {
    submitLeads(
      {
        source: 'Portal: Online profile',
        name: form.name,
        phone: '+62' + form.phone.slice(1),
        user_id: userID,
        property_id: null,
        kind: 'ONE21 Portal',
      },
      {
        onSuccess: () => {
          const textWa = `Hi, saya ingin info lebih lanjut tentang properti ini:\n\n${window.location.href}\n\n_via ONE21 Customer Portal_`;

          const url = `https://api.whatsapp.com/send?text=${encodeURI(
            textWa
          )}&phone=${onlineProfile?.data?.data?.phone}`;

          window.open(url, '_blank');
          callback?.();
        },
        onError: (e) => {
          toast.error(e?.response?.data?.data?.message ?? 'Gagal submit leads');
        },
      }
    );
  };

  const isUserInactive =  useMemo(() => {
    const status = onlineProfile?.data?.data?.is_active;
    return status === false;
  }, [onlineProfile]);

  return (
    <OnlineProfileCtx.Provider
      value={{
        listListing,
        fetchNextPageListListing,
        hasNextPageListListing,
        isLoadingListListing,
        isFetchingNextPageListListing,
        onlineProfile,
        listPropertyCategories,
        filter,
        setFilter,
        initForm,
        isLoadingOnlineProfile,
        initFilterForm,
        initSearchForm,
        handleSubmitLeads,
        id: userID,
        username: onlineProfile?.data?.data?.username,
        isUserInactive
      }}
    >
      {children}
    </OnlineProfileCtx.Provider>
  );
};

const useOnlineProfileCtx = () => {
  const ctx = useContext(OnlineProfileCtx);

  if (!ctx) {
    throw new Error(
      'useOnlineProfileCtx must be used within the OnlineProfileProvider'
    );
  }

  return ctx;
};

export default useOnlineProfileCtx;
